import React, { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
	children: ReactNode;
	fallback?: ReactNode;
}

interface State {
	hasError: boolean;
	error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = { hasError: false, error: null };
	}

	static getDerivedStateFromError(error: Error): State {
		return { hasError: true, error };
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo) {
		console.error('[ErrorBoundary] Caught error:', error, errorInfo);
	}

	render() {
		if (this.state.hasError) {
			if (this.props.fallback) return this.props.fallback;

			return (
				<div className="flex flex-col items-center justify-center min-h-[400px] px-6 text-center">
					<div className="w-16 h-16 rounded-2xl bg-[var(--color-danger-light)] flex items-center justify-center mb-4">
						<i className="pi pi-exclamation-triangle text-2xl text-[var(--color-danger)]" />
					</div>
					<h2 className="text-lg font-semibold text-[var(--color-text-primary)] mb-2">
						Something went wrong
					</h2>
					<p className="text-sm text-[var(--color-text-tertiary)] max-w-md mb-6">
						{this.state.error?.message || 'An unexpected error occurred. Please try refreshing the page.'}
					</p>
					<button
						onClick={() => {
							this.setState({ hasError: false, error: null });
							window.location.reload();
						}}
						className="inline-flex items-center gap-2 px-5 py-2.5 bg-[var(--color-accent)] text-[var(--color-text-inverted)] hover:opacity-90 rounded-xl text-sm font-medium transition-opacity cursor-pointer border-none"
					>
						<i className="pi pi-refresh text-sm" />
						Reload Page
					</button>
				</div>
			);
		}

		return this.props.children;
	}
}

export default ErrorBoundary;
